import React, {
  useState,
  useRef,
  useEffect,
  SyntheticEvent,
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';

import { MetaText } from '~/components/UI/Texts';
import { TINY_MARGIN_PX, SUBMIDDLE_MARGIN_PX, LARGE_RADIUS_PX } from '~/theme';

export type SelectProps = {
  label: string;
  options: { label: string; value: string }[];
  value: string;
  className?: string;
  onChange?: (value: string) => void;
};

export function Select({ label, options, value, className, onChange }: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLButtonElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const handleToggle = (event: SyntheticEvent) => {
    event.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (newValue: string) => {
    onChange?.(newValue);
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  const handleClickOutside = (event: MouseEvent) => {
    event.stopPropagation();

    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setIsOpen(false);
      setFocusedIndex(-1);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    event.stopPropagation();
    event.preventDefault();

    switch (event.code) {
      case 'ArrowDown':
        setIsOpen(true);
        setFocusedIndex((prevIndex) => (prevIndex < options.length - 1 ? prevIndex + 1 : 0));
        break;
      case 'ArrowUp':
        setFocusedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : options.length - 1));
        break;
      case 'Space':
      case 'Enter':
        if (focusedIndex >= 0) {
          handleSelect(options[focusedIndex].value);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleMenuItemClick = (event: ReactMouseEvent<HTMLLIElement>, value: string) => {
    event.stopPropagation();

    handleSelect(value);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && listRef.current) {
      listRef.current.focus();
    }
  }, [isOpen]);

  return (
    <StyledContainer
      id="select-button"
      data-testid="UI/Select"
      className={className}
      ref={buttonRef}
      isOpen={isOpen}
      onClick={handleToggle}
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      role="combobox"
      aria-labelledby="select-button"
    >
      <StyledLabel>{label}</StyledLabel>
      <Box display="flex" alignItems="center">
        <StyledValue>
          {options.find((option) => option.value === value)?.label || 'Select Currency'}
        </StyledValue>
        <StyledChevronIcon isOpen={isOpen} />
      </Box>
      <AnimatePresence initial={false}>
        {isOpen && (
          <StyledMotionDropdown
            ref={listRef}
            id="select-listbox"
            role="listbox"
            tabIndex={-1}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            onKeyDown={handleKeyDown}
          >
            {options.map((option, index) => (
              <StyledMenuItem
                key={option.value}
                role="option"
                aria-selected={value === option.value}
                isSelected={value === option.value}
                isFocused={index === focusedIndex}
                onMouseEnter={() => setFocusedIndex(index)}
                onClick={(e) => handleMenuItemClick(e, option.value)}
              >
                {option.label}
              </StyledMenuItem>
            ))}
          </StyledMotionDropdown>
        )}
      </AnimatePresence>
    </StyledContainer>
  );
}

const StyledLabel = styled(MetaText)`
  line-height: 24px;
`;
const StyledValue = styled(MetaText)`
  line-height: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.text.primary};
`;
const StyledContainer = styled('button')<{ isOpen: boolean }>`
  border: none;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${TINY_MARGIN_PX};
  background-color: ${({ theme }) => theme.palette.common.violetAnalyticsCard};
  padding: ${TINY_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX};
  border-radius: ${({ isOpen }) =>
    isOpen ? `${LARGE_RADIUS_PX} ${LARGE_RADIUS_PX} 0 0` : LARGE_RADIUS_PX};
  position: relative;
  cursor: pointer;
  transition: border-radius 0.3s;
  min-height: 48px;

  &:hover,
  &:focus,
  &:active {
    background-color: ${({ theme }) => alpha(theme.palette.common.violetAnalyticsCard, 0.8)};
  }
`;
const StyledChevronIcon = styled(KeyboardArrowDownIcon, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  fill: ${({ theme }) => theme.palette.common.blue};
  transform: ${({ isOpen }) => (isOpen ? 'rotate(-180deg)' : 'rotate(0deg)')};
  transition: transform 0.3s;
`;
const StyledMotionDropdown = styled(motion.ul)`
  list-style: none;
  margin: 0;
  padding: 0;
  padding-bottom: ${TINY_MARGIN_PX};
  overflow: hidden;
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.common.violetAnalyticsCard};
  border-bottom-left-radius: ${LARGE_RADIUS_PX};
  border-bottom-right-radius: ${LARGE_RADIUS_PX};
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  outline: none;
`;
const StyledMenuItem = styled('li')<{ isSelected: boolean; isFocused: boolean }>`
  padding: 10px;
  color: ${({ theme, isSelected }) =>
    isSelected ? theme.palette.common.blue : theme.palette.common.white};
  background-color: ${({ isFocused, theme }) =>
    isFocused ? theme.palette.action.hover : 'transparent'};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }
`;
